<template>
  <div class="doc-preview-container">
    <div v-if="!pdfViewerError" class="pdf-viewer-wrapper">
      <DocumentPdfViewer
        ref="documentPdfViewer"
        class="DocumentPdfViewer"
        :class="{ loading: !pdfViewerInitialized }"
        :mnemo="mnemo"
        :file-id="fileId"
        :menu-transition="false"
        @initialized="onPdfViewerInitialized"
        @pages-loaded="onPagesLoaded"
        @load-file-from-pdfjs-error="onPdfViewerError"
      />

      <div v-if="!pdfViewerInitialized" class="loading-overlay">
        <v-progress-circular
          indeterminate
          size="48"
        />
        <p class="mt-4 text-body-1">{{ $t('common.DocumentViewer.loadingDocumentLabel') }}</p>
      </div>
    </div>
    <div v-else class="pdf-error-placeholder">
      <div class="d-flex flex-column align-center justify-center h-100 text-text-secondary">
        <app-material-icon name="draft" size="xl" class="mb-4" />
        <p class="text-body-1">{{ $t(`${TRANSLATION_KEY_PREFIX}.pdfUnavailable`) }}</p>
      </div>
    </div>

    <div class="right-panel bg-white" :class="{ 'no-toolbar': pdfViewerError || !pdfViewerInitialized }">
      <!-- Title bar (sticky) -->
      <div class="title-bar px-6 py-4 d-flex align-center justify-space-between">
        <span class="document-title text-text-primary">{{ fileName }}</span>
        <app-button
          data-locator="toggle-all-button"
          type="outlined"
          variant="neutral"
          size="small"
          class="flex-shrink-0"
          :disabled="hasPreviewError || importChecklistFromWordPending"
          @click="toggleAllDescriptions"
        >
          {{ allExpanded ? $t(`${TRANSLATION_KEY_PREFIX}.collapseAll`) : $t(`${TRANSLATION_KEY_PREFIX}.expandAll`) }}
        </app-button>
      </div>

      <!-- Scrollable content -->
      <div class="scrollable-content pa-6 d-flex flex-column row-gap-6">
        <!-- Protected badge info box -->
        <div class="info-box d-flex align-start pa-3 ga-4">
          <ProtegeChip />
          <span class="text--small text-text-secondary protected-description">
            {{ $t(`${TRANSLATION_KEY_PREFIX}.structureDisclaimer`) }}
          </span>
        </div>

        <div class="detection-content">
          <template v-if="importChecklistFromWordPending">
            <v-skeleton-loader type="image" />
            <p class="text--x-small text-text-secondary mx-auto mt-1">{{ $t(`${TRANSLATION_KEY_PREFIX}.pendingMessage`) }}</p>
          </template>
          <template v-else-if="hasPreviewError">
            <app-alert-in-page
              variant="warning"
              :has-left-border="false"
              :dismissible="false"
              class="alert-warning"
            >
              <template #title>
                {{ $t(`${TRANSLATION_KEY_PREFIX}.errorTitle`) }}
              </template>
              {{ $t(`${TRANSLATION_KEY_PREFIX}.errorMessage`) }}
            </app-alert-in-page>
          </template>
          <template v-else>
            <div class="structure-list">
              <SmartImportStructureItem
                v-for="item in preview"
                :key="item.id"
                :item="item"
                :all-expanded="allExpanded"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'

import ProtegeChip from '@/common/ai-assistant/ProtegeChip.vue'
import DocumentPdfViewer from '@/common/document-viewer/DocumentPdfViewer.vue'

import SmartImportStructureItem from './SmartImportStructureItem.vue'

const PREVIEW_PANEL_WIDTH = '784px' // Defined in mockup for checklist preview

const TRANSLATION_KEY_PREFIX = 'project.checklist.dialogs.SmartImportPreview'

export default defineComponent({
  name: 'SmartImportPreview',
  components: {
    DocumentPdfViewer,
    SmartImportStructureItem,
    ProtegeChip,
  },
  props: {
    fileId: {
      type: Number as PropType<number>,
      required: true,
    },
    fileName: {
      type: String,
      required: true,
    },
    preview: {
      type: Object as PropType<any>,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      TRANSLATION_KEY_PREFIX,
      pdfViewerError: false,
      pdfViewerInitialized: false,
      allExpanded: false,
      isInTemporaryFailure: false,
      delayedLoadFileTimerId: -1,
    }
  },
  emits: ['upvote', 'downvote'],
  computed: {
    ...mapGetters('room', { mnemo: 'roomMnemo' }),
    ...mapState('checklist', ['importChecklistFromWordPending']),

    hasPreviewError(): boolean {
      return !this.preview || !Array.isArray(this.preview) || this.preview.length === 0
    },
  },
  methods: {
    toggleAllDescriptions(): void {
      this.allExpanded = !this.allExpanded
    },
    onPdfViewerInitialized(): void {},
    onPagesLoaded(): void {
      this.pdfViewerInitialized = true
      const documentPdfViewer = this.$refs['documentPdfViewer'] as any
      const pdfAppInstance = documentPdfViewer?.getPdfAppInstance?.()
      if (pdfAppInstance?.appConfig?.sidebar?.outerContainer) {
        const { outerContainer } = pdfAppInstance.appConfig.sidebar
        outerContainer.style.setProperty('--right-sidebar-width', PREVIEW_PANEL_WIDTH)
        outerContainer.classList.add('rightSidebarOpen')
      }
      setTimeout(() => {
        documentPdfViewer?.setZoomLevel?.('auto')
        documentPdfViewer?.onViewportChanged?.()
      }, 200)
    },
    onPdfViewerError(error: any): void {
      // In case of a temporary conversion failure, we want to retry loading the file as it might be fixed on the server side
      // This is a temporary solution implemented on 27/04/26
      if (error?.status === 202 && !this.isInTemporaryFailure) {
        this.isInTemporaryFailure = true
        this.delayedLoadFileTimerId = window.setTimeout(() => {
          (this.$refs['documentPdfViewer'] as any)?.loadFile?.()
        }, 2000)
      } else {
        this.pdfViewerError = true
      }
    },
  },
  errorCaptured(): boolean {
    this.pdfViewerError = true
    return false
  },
  beforeUnmount(): void {
    clearTimeout(this.delayedLoadFileTimerId)
    const pdfViewer = (this.$refs['documentPdfViewer'] as any)?.getPdfAppInstance?.()
    if (pdfViewer?.appConfig?.sidebar?.outerContainer) {
      const { outerContainer } = pdfViewer.appConfig.sidebar
      outerContainer.classList.remove('rightSidebarOpen')
      outerContainer.style.removeProperty('--right-sidebar-width')
    }
  },
})
</script>

<style lang="scss" scoped>
.doc-preview-container {
  --smart-import-preview-panel-width: 784px;

  position: relative;
  height: 80vh;
  width: 100%;
  border-bottom: 1px solid rgb(var(--v-theme-border-primary));
  border-top: 1px solid rgb(var(--v-theme-border-primary));

  .pdf-viewer-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: var(--smart-import-preview-panel-width);
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgb(var(--v-theme-surface-secondary));
    z-index: 100000;
  }

  .pdf-error-placeholder {
    width: calc(100% - var(--smart-import-preview-panel-width));
    height: 100%;
    background-color: rgb(var(--v-theme-surface-secondary));
  }

  .right-panel {
    position: absolute;
    top: 58px;
    right: 0;
    width: var(--smart-import-preview-panel-width);
    height: calc(100% - 58px);
    z-index: 1;
    border-left: 1px solid rgb(var(--v-theme-border-primary));
    display: flex;
    flex-direction: column;

    &.no-toolbar {
      top: 0;
      height: 100%;
    }

    .title-bar {
      position: sticky;
      top: 0;
      z-index: 10;
      background-color: white;
      border-bottom: 1px solid rgb(var(--v-theme-border-primary));

      .document-title {
        flex: 0 1 auto;
        word-break: break-word;
        font-size: 18px;
        font-weight: 500;
      }
    }

    .scrollable-content {
      flex: 1;
      overflow-y: auto;
    }

    .info-box {
      background-color: rgb(var(--v-theme-surface-protege-subtle));
      border: 1px solid rgb(var(--v-theme-border-protege));
      border-radius: 8px;
      line-height: 20px;
    }

    .detection-content {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .structure-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .alert-warning {
      background-color: rgb(var(--v-theme-surface-warning));
      border: 1px solid rgb(var(--v-theme-border-warning));
      border-radius: 8px;
    }
  }

  :deep(.DocumentPdfViewer) {
    width: 100%;
    height: 100%;

    &.loading {
      visibility: hidden;
    }
  }

  :deep(#outerContainer.rightSidebarOpen #viewerContainer:not(.pdfPresentationMode)) {
    inset-inline-end: var(--smart-import-preview-panel-width);
    transition-property: inset-inline-start, inset-inline-end;
  }
}
</style>
